import { useMemo, useState } from "react";
import { AuthScreen } from "./components/AuthScreen";
import { Dashboard } from "./components/Dashboard";
import { SceneViewer } from "./components/SceneViewer";
import { ApiClient, type ModelVariantSelection } from "./lib/api";
import type { ModelCoordinateMetadata } from "@factory-twin/contracts";
import { selectViewerProfile, viewerCapabilitiesFromBrowser } from "./lib/viewer-profile";

interface ViewerSource {
  url: string;
  sceneId: string | null;
  name: string;
  model: ModelCoordinateMetadata;
  variant?: ModelVariantSelection;
  revoke?: boolean;
}

const releaseVersion = import.meta.env.VITE_RELEASE_VERSION || "dev";
const deployEnvironment = import.meta.env.VITE_DEPLOY_ENV || "local";
const operationsConsoleEnabled = import.meta.env.VITE_OPERATIONS_CONSOLE === "true";

function ReleaseBadge() {
  return <output className="release-badge" aria-label="deploy version">{deployEnvironment} · {releaseVersion.slice(0, 12)}</output>;
}

export default function App() {
  const [token, setToken] = useState(() => sessionStorage.getItem("factory-twin-token"));
  const [viewer, setViewer] = useState<ViewerSource | null>(null);
  const api = useMemo(() => new ApiClient(() => sessionStorage.getItem("factory-twin-token")), []);

  function authenticated(next: string) { sessionStorage.setItem("factory-twin-token", next); setToken(next); }
  function logout() { sessionStorage.removeItem("factory-twin-token"); setToken(null); }
  function openLocal(file: File) {
    setViewer({
      url: URL.createObjectURL(file),
      sceneId: null,
      name: file.name,
      model: {
        modelVersion: `local:${file.name}:${file.size}:${file.lastModified}`,
        coordinateFrameId: "local",
        coordinateConvention: "RDF",
        alignmentStatus: "CANONICAL",
        alignmentRmse: null,
      },
      revoke: true,
    });
  }
  async function openScene(sceneId: string) {
    const profile = selectViewerProfile(viewerCapabilitiesFromBrowser());
    const { url, expiresIn: _expiresIn, variant, ...model } = await api.getModelUrl(sceneId, profile.assetQuality);
    setViewer({ url: api.resolveUrl(url), sceneId, name: sceneId.slice(0, 8).toUpperCase(), model, variant });
  }
  async function requestLowQualityScene(sceneId: string) {
    const { url, expiresIn: _expiresIn, variant, ...model } = await api.getModelUrl(sceneId, "low");
    setViewer((current) => current?.sceneId === sceneId
      ? { ...current, url: api.resolveUrl(url), model, variant }
      : current);
  }
  function closeViewer() { if (viewer?.revoke) URL.revokeObjectURL(viewer.url); setViewer(null); }

  if (viewer) return <><SceneViewer api={api} {...viewer} sourceUrl={viewer.url} sourceName={viewer.name} onExit={closeViewer} onRequestLowQuality={viewer.sceneId ? () => requestLowQualityScene(viewer.sceneId!) : undefined} /><ReleaseBadge /></>;
  if (!token) return <><AuthScreen api={api} onAuthenticated={authenticated} onOpenLocal={openLocal} /><ReleaseBadge /></>;
  return <><Dashboard api={api} operationsEnabled={operationsConsoleEnabled} onOpenScene={(sceneId) => void openScene(sceneId)} onOpenLocal={openLocal} onLogout={logout} /><ReleaseBadge /></>;
}
